import { useCallback, useEffect, useMemo, useState } from 'react'
import { ExternalLink, RefreshCw, Search, Shield } from 'lucide-react'
import { getIamPortalOrigin } from '../../../auth/portal'
import { clearOidcReauthorization, clearSession, loadSession } from '../../../auth/state'
import { reauthorizeAfterUnauthorized } from '../../../auth/oidc'
import { Card } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { useI18n } from '../../../utils/i18n'
import { unwrapIamAdminPayload } from './adminUsers/iam-admin-response.js'

async function apiJson(url, token, init = {}) {
  const headers = new Headers(init.headers || {})
  headers.set('Accept', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')

  const resp = await fetch(url, { ...init, headers, cache: 'no-store' })
  const text = await resp.text()
  let data = {}
  try {
    data = text ? JSON.parse(text) : {}
  } catch {
    data = { raw: text }
  }
  if (!resp.ok) {
    if (resp.status === 401 && reauthorizeAfterUnauthorized()) {
      const err = new Error('reauthorizing')
      err.reauthorizing = true
      throw err
    }
    const err = new Error(String(data?.msg || data?.error || resp.status))
    err.status = resp.status
    err.data = data
    throw err
  }
  clearOidcReauthorization()
  return unwrapIamAdminPayload(data)
}

function isReauthorizing(error) {
  return Boolean(error?.reauthorizing)
}

function Badge({ tone = 'neutral', children }) {
  const tones = {
    neutral: 'border-brand-textDark/10 bg-brand-surface text-brand-textDark',
    ok: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    warn: 'border-amber-200 bg-amber-50 text-amber-800',
    danger: 'border-rose-200 bg-rose-50 text-rose-700',
  }
  return (
    <span className={`inline-flex items-center px-3 py-1.5 rounded-xl border-2 text-xs font-extrabold ${tones[tone] || tones.neutral}`}>
      {children}
    </span>
  )
}

export default function AdminUsers() {
  const { tx } = useI18n()
  const session = useMemo(() => loadSession(), [])
  const token = String(session?.tokens?.accessToken || session?.tokens?.idToken || '')
  const iamPortal = useMemo(() => getIamPortalOrigin(), [])

  const [state, setState] = useState({ loading: true, error: '', data: null })
  const [users, setUsers] = useState([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [usersError, setUsersError] = useState('')
  const [query, setQuery] = useState('')

  const reLogin = useCallback(() => {
    clearSession()
    window.location.replace('/login?fresh=1')
  }, [])

  const loadState = useCallback(async () => {
    if (!token) {
      setState({ loading: false, error: 'missing_token', data: null })
      return
    }
    setState((s) => ({ ...s, loading: true, error: '' }))
    try {
      const data = await apiJson('/iam-admin/state', token)
      setState({ loading: false, error: '', data })
      return data
    } catch (e) {
      if (isReauthorizing(e)) return null
      setState({ loading: false, error: String(e?.message || e), data: null })
      return null
    }
  }, [token])

  const loadUsers = useCallback(async () => {
    setUsersLoading(true)
    setUsersError('')
    try {
      const data = await apiJson('/iam-admin/users', token)
      setUsers(Array.isArray(data?.users) ? data.users : [])
    } catch (e) {
      if (isReauthorizing(e)) return
      setUsersError(String(e?.message || e))
      setUsers([])
    } finally {
      setUsersLoading(false)
    }
  }, [token])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const st = await loadState()
      if (cancelled) return
      if (st?.ok && st?.isAdmin) loadUsers()
    })()
    return () => {
      cancelled = true
    }
  }, [loadState, loadUsers])

  const setAdmin = useCallback(
    async (user, next) => {
      const target = user?.name || user?.email || user?.id || ''
      const ok = window.confirm(`${next ? tx({ zh: '授予 ADMIN 给', en: 'Grant ADMIN to' }) : tx({ zh: '撤销 ADMIN 从', en: 'Revoke ADMIN from' })} ${target} ?`)
      if (!ok) return
      setUsersError('')
      try {
        await apiJson(`/iam-admin/users/${encodeURIComponent(String(user.id))}/admin`, token, {
          method: 'POST',
          body: JSON.stringify({ isAdmin: Boolean(next) }),
        })
        setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, isAdmin: Boolean(next) } : u)))
      } catch (e) {
        if (isReauthorizing(e)) return
        setUsersError(String(e?.message || e))
      }
    },
    [token, tx],
  )

  const filteredUsers = useMemo(() => {
    const q = String(query || '').trim().toLowerCase()
    if (!q) return users
    return users.filter((u) => {
      return [u?.name, u?.email, u?.sub, u?.id].some((v) => String(v || '').toLowerCase().includes(q))
    })
  }, [users, query])

  const st = state.data

  return (
    <div className="space-y-4">
      <Card className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-brand-textDark dark:text-slate-100 font-extrabold">{tx({ zh: '账号管理', en: 'Accounts' })}</div>
            <div className="mt-1 text-brand-text dark:text-slate-300 text-sm font-bold">
              {tx({
                zh: '这里管理 “谁可以进入控制台”。权限基于 IAM 角色：ADMIN。',
                en: 'Manage who can access this console (IAM role: ADMIN).',
              })}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs font-extrabold text-brand-text dark:text-slate-300">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border-2 border-brand-textDark/10 bg-brand-surface dark:border-slate-800 dark:bg-slate-900">
                <Shield className="w-4 h-4 text-brand-text dark:text-slate-300" />
                {tx({ zh: '需要角色', en: 'Requires role' })}:{' '}
                <span className="text-brand-textDark dark:text-slate-100">ADMIN</span>
              </span>
              {state.loading ? <Badge tone="neutral">{tx({ zh: '加载中…', en: 'Loading…' })}</Badge> : null}
              {!state.loading && state.error ? <Badge tone="danger">{state.error}</Badge> : null}
              {!state.loading && !state.error && st?.ok ? (
                <Badge tone={st.isAdmin ? 'ok' : st.hasAdmin ? 'danger' : 'warn'}>
                  {st.isAdmin ? tx({ zh: '你是 ADMIN', en: 'You are ADMIN' }) : st.hasAdmin ? tx({ zh: '你不是 ADMIN', en: 'Not ADMIN' }) : tx({ zh: '未初始化', en: 'Not initialized' })}
                </Badge>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              as="a"
              href={iamPortal}
              target="_blank"
              rel="noreferrer"
              variant="secondary"
              size="sm"
              title={tx({ zh: '在 IAM Admin 配置角色/绑定', en: 'Manage roles/bindings in IAM Admin' })}
            >
              <ExternalLink className="w-4 h-4" />
              {tx({ zh: '打开 IAM Admin', en: 'Open IAM Admin' })}
            </Button>
            {!token ? (
              <Button variant="neutral" size="sm" onClick={reLogin} title={tx({ zh: '清理会话并重新登录', en: 'Clear session and re-login' })}>
                {tx({ zh: '重新登录', en: 'Re-login' })}
              </Button>
            ) : null}
          </div>
        </div>
      </Card>

      {!state.loading && st?.ok && !st?.hasAdmin ? (
        <Card className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="text-brand-textDark dark:text-slate-100 font-extrabold">{tx({ zh: '等待 IAM 管理员授权', en: 'IAM operator provisioning required' })}</div>
              <div className="mt-1 text-brand-text dark:text-slate-300 text-sm font-bold">
                {tx({
                  zh: '当前应用还没有 ADMIN。请联系有权限的 IAM 管理员，为此应用分配首位管理员；本页面不提供自助提权。',
                  en: 'This application has no ADMIN. Ask an authorized IAM operator to assign the first administrator. Self-service privilege escalation is not available here.',
                })}
              </div>
            </div>
          </div>
          {usersError ? <div className="mt-3 text-sm font-bold text-rose-600">{usersError}</div> : null}
        </Card>
      ) : null}

      {!state.loading && st?.ok && st?.hasAdmin && !st?.isAdmin ? (
        <Card className="p-6">
          <div className="text-brand-textDark dark:text-slate-100 font-extrabold">{tx({ zh: '你没有权限', en: 'Not authorized' })}</div>
          <div className="mt-1 text-brand-text dark:text-slate-300 text-sm font-bold">
            {tx({
              zh: '请让现有管理员在本页给你授予 ADMIN。',
              en: 'Ask an existing ADMIN to grant you ADMIN access here.',
            })}
          </div>
          {st?.me ? (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs font-black">
              <div className="px-4 py-3 rounded-2xl bg-brand-surface border-2 border-brand-textDark/10 dark:border-slate-800 dark:bg-slate-900">
                <div className="text-brand-text dark:text-slate-400">{tx({ zh: 'Name', en: 'Name' })}</div>
                <div className="mt-1 text-brand-textDark dark:text-slate-100 break-words">{st.me.name || '-'}</div>
              </div>
              <div className="px-4 py-3 rounded-2xl bg-brand-surface border-2 border-brand-textDark/10 dark:border-slate-800 dark:bg-slate-900">
                <div className="text-brand-text dark:text-slate-400">Email</div>
                <div className="mt-1 text-brand-textDark dark:text-slate-100 break-words">{st.me.email || '-'}</div>
              </div>
              <div className="px-4 py-3 rounded-2xl bg-brand-surface border-2 border-brand-textDark/10 dark:border-slate-800 dark:bg-slate-900">
                <div className="text-brand-text dark:text-slate-400">Sub</div>
                <div className="mt-1 text-brand-textDark dark:text-slate-100 break-words">{st.me.sub || '-'}</div>
              </div>
            </div>
          ) : null}
        </Card>
      ) : null}

      {!state.loading && st?.ok && st?.isAdmin ? (
        <Card className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="min-w-0">
              <div className="text-brand-textDark dark:text-slate-100 font-extrabold">{tx({ zh: '用户列表', en: 'Users' })}</div>
              <div className="mt-1 text-brand-text dark:text-slate-300 text-sm font-bold">
                {tx({ zh: '点击按钮授予/撤销 ADMIN。', en: 'Grant/revoke ADMIN with the button.' })}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 text-brand-text absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={tx({ zh: '搜索 name / email / sub…', en: 'Search name / email / sub…' })}
                  className="w-72 max-w-[70vw] pl-9 pr-3 py-2.5 rounded-2xl border-2 border-brand-textDark/10 bg-brand-surface text-sm font-bold text-brand-textDark placeholder:text-brand-text/60 focus:outline-none focus:border-brand-yellowDark dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500"
                />
              </div>
              <Button
                variant="neutral"
                size="sm"
                onClick={async () => {
                  const next = await loadState()
                  if (next?.ok && next?.isAdmin) await loadUsers()
                }}
                disabled={usersLoading}
                title={tx({ zh: '刷新', en: 'Refresh' })}
              >
                <RefreshCw className="w-4 h-4" />
                {tx({ zh: '刷新', en: 'Refresh' })}
              </Button>
            </div>
          </div>

          {usersError ? <div className="mt-3 text-sm font-bold text-rose-600">{usersError}</div> : null}

          <div className="mt-5">
            {usersLoading ? (
              <div className="text-sm font-bold text-brand-text dark:text-slate-400">{tx({ zh: '加载中…', en: 'Loading…' })}</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredUsers.map((u) => {
                  return (
                    <div
                      key={u.id}
                      className="bg-brand-surface rounded-3xl p-5 border-2 border-brand-textDark/10 shadow-[0_20px_60px_-10px_rgba(0,0,0,0.08)] dark:bg-slate-950 dark:border-slate-800"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-brand-textDark dark:text-slate-100 font-extrabold truncate">{u.name || u.email || u.sub || u.id}</div>
                          <div className="mt-1 text-brand-text dark:text-slate-400 text-sm font-bold truncate">{u.email || u.sub || '-'}</div>
                        </div>
                        {u.isAdmin ? <Badge tone="ok">ADMIN</Badge> : <Badge tone="neutral">—</Badge>}
                      </div>

                      <div className="mt-4 flex items-center justify-between gap-3">
                        <Badge tone={String(u.status).toLowerCase() === 'active' ? 'ok' : 'warn'}>{u.status || 'unknown'}</Badge>
                        <Button
                          size="sm"
                          variant={u.isAdmin ? 'neutral' : 'secondary'}
                          onClick={() => setAdmin(u, !u.isAdmin)}
                        >
                          {u.isAdmin ? tx({ zh: '撤销 ADMIN', en: 'Revoke' }) : tx({ zh: '授予 ADMIN', en: 'Grant' })}
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </Card>
      ) : null}
    </div>
  )
}
